/**
 * Zaawansowany generator PDF z AI i wizualizacjami 3D
 * Tworzy profesjonalne oferty w 2 minuty zamiast 30
 */

import { Offer, CalculatorItem } from '@/types/Offer';
import { AIProductDescriptionGenerator } from './productDescriptionGenerator';
import { deliveryRegions, expositorTypes } from '@/constants/materials';
import { additionalOptions } from '@/constants/options';

interface EnhancedPDFOptions {
  includeDescriptions?: boolean;
  include3D?: boolean;
  includeQRCode?: boolean;
  template?: 'modern' | 'classic' | 'premium';
  language?: 'pl' | 'en';
}

export class EnhancedPDFGenerator {
  private static readonly TEMPLATES = {
    modern: {
      primaryColor: '#f97316',
      secondaryColor: '#fbbf24',
      fontFamily: 'Inter, sans-serif',
      borderRadius: '8px'
    },
    classic: {
      primaryColor: '#1e40af',
      secondaryColor: '#3b82f6',
      fontFamily: 'Georgia, serif',
      borderRadius: '0px'
    },
    premium: {
      primaryColor: '#7c3aed',
      secondaryColor: '#a78bfa',
      fontFamily: 'Playfair Display, serif',
      borderRadius: '12px'
    }
  };

  /**
   * Generuje zaawansowany PDF z opisami AI i wizualizacjami
   */
  static async generateEnhancedPDF(
    offer: Offer,
    options: EnhancedPDFOptions = {}
  ): Promise<string> {
    const {
      includeDescriptions = true,
      include3D = true,
      includeQRCode = true,
      template = 'modern',
      language = 'pl'
    } = options;

    const theme = this.TEMPLATES[template];
    
    // Generuj opisy AI dla każdego produktu
    const productDescriptions = includeDescriptions 
      ? offer.items.map(item => ({
          item,
          description: AIProductDescriptionGenerator.generateDescription(item)
        }))
      : [];

    return this.generateHTML(offer, productDescriptions, theme, options);
  }

  /**
   * Generuje HTML dla PDF
   */
  private static generateHTML(
    offer: Offer,
    productDescriptions: any[],
    theme: any,
    options: EnhancedPDFOptions
  ): string {
    const coverPage = this.generateCoverPage(offer, theme);
    const summaryPage = this.generateSummaryPage(offer, theme);
    const productPages = this.generateProductPages(offer, productDescriptions, theme, options);
    const termsPage = this.generateTermsPage(offer, theme);
    const footerPage = this.generateFooterPage(offer, theme, options);

    return `
      <!DOCTYPE html>
      <html lang="${options.language}">
      <head>
        <meta charset="UTF-8">
        <title>Oferta ${offer.number} - PlexiSystem</title>
        ${this.generateStyles(theme)}
      </head>
      <body>
        ${coverPage}
        ${summaryPage}
        ${productPages}
        ${termsPage}
        ${footerPage}
      </body>
      </html>
    `;
  }

  /**
   * Generuje style CSS
   */
  private static generateStyles(theme: any): string {
    return `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700;900&family=Playfair+Display:wght@400;700;900&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: ${theme.fontFamily};
          color: #1f2937;
          line-height: 1.6;
          background: white;
        }
        
        .page {
          min-height: 100vh;
          padding: 40px;
          position: relative;
          page-break-after: always;
        }
        
        .cover-page {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          background: linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.secondaryColor} 100%);
          color: white;
        }
        
        .logo {
          font-size: 4rem;
          font-weight: 900;
          margin-bottom: 2rem;
          text-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
        
        .offer-number {
          font-size: 2.5rem;
          font-weight: 700;
          margin: 1rem 0;
        }
        
        .client-name {
          font-size: 1.8rem;
          font-weight: 300;
          margin: 2rem 0;
          opacity: 0.95;
        }
        
        .header {
          display: flex;
          justify-content: space-between;
          align-items: start;
          margin-bottom: 3rem;
          padding-bottom: 1rem;
          border-bottom: 3px solid ${theme.primaryColor};
        }
        
        .section {
          margin-bottom: 2rem;
        }
        
        .section-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: ${theme.primaryColor};
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .product-card {
          background: #f9fafb;
          border-radius: ${theme.borderRadius};
          padding: 2rem;
          margin-bottom: 2rem;
          border: 1px solid #e5e7eb;
          page-break-inside: avoid;
        }
        
        .product-header {
          display: flex;
          justify-content: space-between;
          align-items: start;
          margin-bottom: 1.5rem;
        }
        
        .product-name {
          font-size: 1.3rem;
          font-weight: 700;
          color: #111827;
        }
        
        .product-price {
          font-size: 1.5rem;
          font-weight: 700;
          color: ${theme.primaryColor};
        }
        
        .product-visualization {
          width: 100%;
          height: 300px;
          background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
          border-radius: ${theme.borderRadius};
          margin: 1.5rem 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          font-size: 1.2rem;
        }
        
        .specifications {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin: 1.5rem 0;
        }
        
        .spec-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.95rem;
        }
        
        .spec-label {
          font-weight: 600;
          color: #6b7280;
        }
        
        .spec-value {
          color: #111827;
        }
        
        .advantages {
          background: #ecfdf5;
          border-left: 4px solid #10b981;
          padding: 1rem 1.5rem;
          margin: 1.5rem 0;
          border-radius: ${theme.borderRadius};
        }
        
        .advantages-title {
          font-weight: 700;
          color: #047857;
          margin-bottom: 0.5rem;
        }
        
        .advantages-list {
          list-style: none;
          color: #065f46;
        }
        
        .advantages-list li {
          padding: 0.25rem 0;
        }
        
        .applications {
          background: #fef3c7;
          border-left: 4px solid #f59e0b;
          padding: 1rem 1.5rem;
          margin: 1.5rem 0;
          border-radius: ${theme.borderRadius};
        }
        
        .applications-title {
          font-weight: 700;
          color: #92400e;
          margin-bottom: 0.5rem;
        }
        
        .applications-list {
          list-style: none;
          color: #78350f;
        }
        
        .summary-table {
          width: 100%;
          border-collapse: collapse;
          margin: 2rem 0;
        }
        
        .summary-table th {
          background: ${theme.primaryColor};
          color: white;
          padding: 1rem;
          text-align: left;
          font-weight: 600;
        }
        
        .summary-table td {
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .summary-table tr:hover {
          background: #f9fafb;
        }
        
        .total-row {
          background: #fef3c7;
          font-weight: 700;
          font-size: 1.2rem;
        }
        
        .qr-code {
          width: 150px;
          height: 150px;
          background: white;
          border: 2px solid ${theme.primaryColor};
          padding: 10px;
          border-radius: ${theme.borderRadius};
        }
        
        .footer {
          text-align: center;
          color: #6b7280;
          font-size: 0.9rem;
          margin-top: 3rem;
        }
        
        @media print {
          body { margin: 0; }
          .page { padding: 20px; }
          .cover-page { 
            -webkit-print-color-adjust: exact; 
            print-color-adjust: exact; 
          }
        }
      </style>
    `;
  }

  /**
   * Generuje stronę tytułową
   */
  private static generateCoverPage(offer: Offer, theme: any): string {
    const date = new Date(offer.date);
    const formattedDate = date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `
      <div class="page cover-page">
        <div class="logo">PlexiSystem</div>
        <div style="font-size: 1.2rem; font-weight: 300; opacity: 0.9;">
          Profesjonalne wyroby z plexi i tworzyw sztucznych
        </div>
        
        <div style="margin: 4rem 0;">
          <div class="offer-number">OFERTA ${offer.number}</div>
          ${offer.projectName ? `
            <div style="font-size: 1.5rem; margin-top: 1rem; font-weight: 500;">
              ${offer.projectName}
            </div>
          ` : ''}
        </div>
        
        <div class="client-name">
          dla<br>
          <strong>${offer.client.name || 'Szanowny Kliencie'}</strong>
        </div>
        
        <div style="margin-top: auto; padding-top: 4rem;">
          <div style="font-size: 1rem; opacity: 0.9;">${formattedDate}</div>
          <div style="font-size: 0.9rem; opacity: 0.8; margin-top: 0.5rem;">
            Ważna do: ${new Date(offer.validUntil).toLocaleDateString('pl-PL')}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generuje stronę podsumowania
   */
  private static generateSummaryPage(offer: Offer, theme: any): string {
    return `
      <div class="page">
        <div class="header">
          <div>
            <div style="font-size: 2rem; font-weight: 700; color: ${theme.primaryColor};">
              PlexiSystem
            </div>
            <div style="margin-top: 0.5rem; color: #6b7280;">
              Ks. Dr. Leona Heyke 11<br>
              84-206 Nowy Dwór Wejherowski<br>
              NIP: 588-239-62-72<br>
              Tel: 884 042 107
            </div>
          </div>
          <div style="text-align: right;">
            <div style="font-weight: 600; margin-bottom: 0.5rem;">Dane klienta:</div>
            <div>
              <strong>${offer.client.name}</strong><br>
              ${offer.client.address}<br>
              NIP: ${offer.client.nip || 'Brak'}<br>
              ${offer.client.email ? `Email: ${offer.client.email}<br>` : ''}
              ${offer.client.phone ? `Tel: ${offer.client.phone}` : ''}
            </div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">
            📊 Podsumowanie oferty
          </div>
          
          <table class="summary-table">
            <thead>
              <tr>
                <th style="width: 10%;">Lp.</th>
                <th style="width: 40%;">Produkt</th>
                <th style="width: 15%;">Ilość</th>
                <th style="width: 15%;">Cena jedn.</th>
                <th style="width: 20%;">Wartość</th>
              </tr>
            </thead>
            <tbody>
              ${offer.items.map((item, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>
                    <strong>${this.getProductName(item)}</strong>
                    ${this.getProductSubtitle(item)}
                  </td>
                  <td style="text-align: center;">${item.quantity} szt.</td>
                  <td style="text-align: right;">${item.unitPrice.toFixed(2)} zł</td>
                  <td style="text-align: right; font-weight: 600;">
                    ${item.totalPrice.toFixed(2)} zł
                  </td>
                </tr>
              `).join('')}
              
              <tr>
                <td colspan="4" style="text-align: right; font-weight: 600;">
                  Wartość netto:
                </td>
                <td style="text-align: right; font-weight: 700;">
                  ${offer.totalNet.toFixed(2)} zł
                </td>
              </tr>
              
              ${offer.discount > 0 ? `
                <tr>
                  <td colspan="4" style="text-align: right;">
                    Rabat ${offer.discount}%:
                  </td>
                  <td style="text-align: right; color: ${theme.primaryColor};">
                    -${offer.discountValue.toFixed(2)} zł
                  </td>
                </tr>
              ` : ''}
              
              ${offer.deliveryCost > 0 ? `
                <tr>
                  <td colspan="4" style="text-align: right;">
                    Koszt dostawy:
                  </td>
                  <td style="text-align: right;">
                    ${offer.deliveryCost.toFixed(2)} zł
                  </td>
                </tr>
              ` : ''}
              
              <tr class="total-row">
                <td colspan="4" style="text-align: right; padding: 1.5rem 1rem;">
                  RAZEM NETTO:
                </td>
                <td style="text-align: right; padding: 1.5rem 1rem;">
                  ${(offer.totalNetAfterDiscount + offer.deliveryCost).toFixed(2)} zł
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div style="background: #ecfdf5; padding: 1.5rem; border-radius: ${theme.borderRadius}; margin-top: 2rem;">
          <div style="font-weight: 700; color: #047857; margin-bottom: 0.5rem;">
            ⏱️ Szybka realizacja
          </div>
          <div style="color: #065f46;">
            Czas realizacji: <strong>${offer.terms.deliveryTime}</strong><br>
            Dostawa: <strong>${offer.terms.deliveryMethod}</strong>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generuje strony produktów z opisami AI
   */
  private static generateProductPages(
    offer: Offer,
    productDescriptions: any[],
    theme: any,
    options: EnhancedPDFOptions
  ): string {
    if (!options.includeDescriptions || productDescriptions.length === 0) {
      return '';
    }

    return productDescriptions.map(({ item, description }, index) => `
      <div class="page">
        <div class="header">
          <div style="font-size: 1.5rem; font-weight: 700; color: ${theme.primaryColor};">
            Produkt ${index + 1} z ${offer.items.length}
          </div>
          <div style="text-align: right; color: #6b7280;">
            Oferta ${offer.number}
          </div>
        </div>
        
        <div class="product-card">
          <div class="product-header">
            <div>
              <div class="product-name">${this.getProductName(item)}</div>
              <div style="color: #6b7280; margin-top: 0.5rem;">
                ${this.getProductSubtitle(item)}
              </div>
            </div>
            <div class="product-price">
              ${item.totalPrice.toFixed(2)} zł
            </div>
          </div>
          
          ${options.include3D ? `
            <div class="product-visualization">
              <div>
                🎨 Wizualizacja 3D<br>
                <span style="font-size: 0.9rem;">
                  (Dostępna w wersji online)
                </span>
              </div>
            </div>
          ` : ''}
          
          <div class="specifications">
            ${description.specifications.map((spec: string) => `
              <div class="spec-item">
                <span>${spec}</span>
              </div>
            `).join('')}
          </div>
          
          <div style="margin: 2rem 0;">
            <div style="font-weight: 700; margin-bottom: 1rem;">Opis techniczny:</div>
            <div style="color: #4b5563; line-height: 1.8;">
              ${description.technical}
            </div>
          </div>
          
          <div style="margin: 2rem 0;">
            <div style="font-weight: 700; margin-bottom: 1rem;">Dlaczego warto:</div>
            <div style="color: #4b5563; line-height: 1.8;">
              ${description.marketing}
            </div>
          </div>
          
          <div class="advantages">
            <div class="advantages-title">Zalety produktu:</div>
            <ul class="advantages-list">
              ${description.advantages.slice(0, 6).map((adv: string) => `
                <li>${adv}</li>
              `).join('')}
            </ul>
          </div>
          
          <div class="applications">
            <div class="applications-title">Zastosowania:</div>
            <ul class="applications-list">
              ${description.applications.slice(0, 6).map((app: string) => `
                <li>${app}</li>
              `).join('')}
            </ul>
          </div>
        </div>
      </div>
    `).join('');
  }

  /**
   * Generuje stronę z warunkami
   */
  private static generateTermsPage(offer: Offer, theme: any): string {
    return `
      <div class="page">
        <div class="section">
          <div class="section-title">
            📋 Warunki realizacji
          </div>
          
          <div style="background: #f9fafb; padding: 2rem; border-radius: ${theme.borderRadius};">
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 2rem;">
              <div>
                <div style="font-weight: 700; color: ${theme.primaryColor}; margin-bottom: 0.5rem;">
                  ⏱️ Termin realizacji
                </div>
                <div>${offer.terms.deliveryTime}</div>
              </div>
              
              <div>
                <div style="font-weight: 700; color: ${theme.primaryColor}; margin-bottom: 0.5rem;">
                  🚚 Sposób dostawy
                </div>
                <div>${offer.terms.deliveryMethod}</div>
              </div>
              
              <div>
                <div style="font-weight: 700; color: ${theme.primaryColor}; margin-bottom: 0.5rem;">
                  💳 Warunki płatności
                </div>
                <div>${offer.terms.paymentTerms}</div>
              </div>
              
              <div>
                <div style="font-weight: 700; color: ${theme.primaryColor}; margin-bottom: 0.5rem;">
                  🛡️ Gwarancja
                </div>
                <div>${offer.terms.warranty}</div>
              </div>
              
              <div>
                <div style="font-weight: 700; color: ${theme.primaryColor}; margin-bottom: 0.5rem;">
                  📅 Ważność oferty
                </div>
                <div>${offer.terms.validity}</div>
              </div>
              
              <div>
                <div style="font-weight: 700; color: ${theme.primaryColor}; margin-bottom: 0.5rem;">
                  📦 Region dostawy
                </div>
                <div>${deliveryRegions.find(r => r.id === offer.deliveryRegion)?.name || 'Do ustalenia'}</div>
              </div>
            </div>
            
            ${offer.comment ? `
              <div style="margin-top: 2rem; padding-top: 2rem; border-top: 1px solid #e5e7eb;">
                <div style="font-weight: 700; color: ${theme.primaryColor}; margin-bottom: 0.5rem;">
                  💬 Uwagi dodatkowe:
                </div>
                <div style="color: #4b5563;">
                  ${offer.comment}
                </div>
              </div>
            ` : ''}
          </div>
        </div>
        
        <div class="section" style="margin-top: 3rem;">
          <div class="section-title">
            ✅ Dlaczego PlexiSystem?
          </div>
          
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 2rem;">
            <div style="background: #ecfdf5; padding: 1.5rem; border-radius: ${theme.borderRadius};">
              <div style="font-weight: 700; color: #047857; margin-bottom: 0.5rem;">
                🏆 Doświadczenie
              </div>
              <div style="color: #065f46; font-size: 0.95rem;">
                Ponad 10 lat na rynku, tysiące zrealizowanych projektów
              </div>
            </div>
            
            <div style="background: #fef3c7; padding: 1.5rem; border-radius: ${theme.borderRadius};">
              <div style="font-weight: 700; color: #92400e; margin-bottom: 0.5rem;">
                ⚡ Szybka realizacja
              </div>
              <div style="color: #78350f; font-size: 0.95rem;">
                Własny park maszynowy CNC, produkcja na miejscu
              </div>
            </div>
            
            <div style="background: #ede9fe; padding: 1.5rem; border-radius: ${theme.borderRadius};">
              <div style="font-weight: 700; color: #5b21b6; margin-bottom: 0.5rem;">
                💎 Najwyższa jakość
              </div>
              <div style="color: #4c1d95; font-size: 0.95rem;">
                Certyfikowane materiały, precyzja wykonania ±0.1mm
              </div>
            </div>
            
            <div style="background: #fee2e2; padding: 1.5rem; border-radius: ${theme.borderRadius};">
              <div style="font-weight: 700; color: #991b1b; margin-bottom: 0.5rem;">
                🤝 Wsparcie
              </div>
              <div style="color: #7f1d1d; font-size: 0.95rem;">
                Doradztwo techniczne na każdym etapie projektu
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generuje stopkę z QR kodem
   */
  private static generateFooterPage(offer: Offer, theme: any, options: EnhancedPDFOptions): string {
    return `
      <div class="page" style="display: flex; flex-direction: column; justify-content: center; align-items: center;">
        <div style="text-align: center; max-width: 600px;">
          <div style="font-size: 2rem; font-weight: 700; color: ${theme.primaryColor}; margin-bottom: 2rem;">
            Dziękujemy za zainteresowanie!
          </div>
          
          ${offer.shareLink && options.includeQRCode ? `
            <div style="margin: 3rem 0;">
              <div class="qr-code" style="margin: 0 auto;">
                <div style="padding: 20px; text-align: center; color: #6b7280;">
                  📱 QR Code<br>
                  <span style="font-size: 0.8rem;">
                    Zeskanuj aby zaakceptować online
                  </span>
                </div>
              </div>
              <div style="margin-top: 1rem; font-size: 0.9rem; color: #6b7280;">
                ${offer.shareLink}
              </div>
            </div>
          ` : ''}
          
          <div style="background: #f9fafb; padding: 2rem; border-radius: ${theme.borderRadius}; margin: 3rem 0;">
            <div style="font-weight: 700; margin-bottom: 1rem;">Kontakt:</div>
            <div style="color: #4b5563;">
              <strong>${offer.salesperson.name}</strong><br>
              Tel: ${offer.salesperson.phone}<br>
              Email: ${offer.salesperson.email}
            </div>
          </div>
          
          <div class="footer">
            <div style="font-weight: 700; font-size: 1.1rem; margin-bottom: 1rem;">
              PlexiSystem S.C.
            </div>
            <div>
              Ks. Dr. Leona Heyke 11, 84-206 Nowy Dwór Wejherowski<br>
              NIP: 588-239-62-72 | REGON: 220833568<br>
              www.plexisystem.pl | biuro@plexisystem.pl
            </div>
            
            <div style="margin-top: 2rem; padding: 1rem; background: #f3f4f6; border-radius: ${theme.borderRadius};">
              <div style="font-size: 0.8rem; color: #6b7280;">
                Oferta wygenerowana: ${new Date().toLocaleDateString('pl-PL')} 
                | PlexiSystem CRM v2.0 
                | AI Enhanced
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Pomocnicze funkcje
   */
  private static getProductName(item: CalculatorItem): string {
    if ('isCustom' in item && item.isCustom) {
      return (item as any).name;
    }
    if ('isMagnet' in item && item.isMagnet) {
      return 'Magnesy neodymowe';
    }
    return item.productName || 'Produkt';
  }

  private static getProductSubtitle(item: CalculatorItem): string {
    if ('isCustom' in item && item.isCustom) {
      return `<br><small style="color: #6b7280;">Produkt nietypowy</small>`;
    }
    if ('isMagnet' in item && item.isMagnet) {
      return `<br><small style="color: #6b7280;">Ø${(item as any).diameter}×${(item as any).thickness}mm</small>`;
    }
    if (item.dimensions) {
      return `<br><small style="color: #6b7280;">${item.dimensions.width}×${item.dimensions.height}${item.dimensions.depth ? `×${item.dimensions.depth}` : ''} mm | ${item.materialName} ${item.thickness}mm</small>`;
    }
    return '';
  }
}

/**
 * Eksportuj jako domyślną funkcję dla kompatybilności
 */
export async function generateEnhancedPDF(offer: Offer, options?: EnhancedPDFOptions): Promise<Blob> {
  const html = await EnhancedPDFGenerator.generateEnhancedPDF(offer, options);
  return new Blob([html], { type: 'text/html' });
}
